/**
 * TierDefinitionsPage — `/franchise/tiers`.
 *
 * Read-only catalogue of TIER_1 / TIER_2 / TIER_3 from
 * `GET /v1/franchise/tiers`, with admin inline-edit of dailyTxnLimit + active
 * via `PATCH /v1/franchise/tiers/:code` (`FranchiseTier/write`).
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { listTiers, updateTier } from '../../lib/api/franchise';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

export function TierDefinitionsPage(): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [editLimit, setEditLimit] = useState('');
  const [actionErr, setActionErr] = useState<string | null>(null);

  const tiers = useQuery({
    queryKey: ['franchise', 'tiers'],
    queryFn: () => listTiers({ active: true }),
    retry: false,
    staleTime: 60_000,
  });

  const denied = tiers.isError && tiers.error instanceof ApiError &&
    (tiers.error.status === 401 || tiers.error.status === 403);

  const save = useMutation({
    mutationFn: (code: string) => updateTier(code, { dailyTxnLimit: editLimit }),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ['franchise', 'tiers'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tier definitions"
        description="Franchise tier catalogue — allowed services, daily limits, permissions."
      />

      {tiers.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Franchise/read</code>.
        </DeferredNotice>
      ) : tiers.isError ? (
        <DeferredNotice title="Service unreachable">{(tiers.error as Error).message}</DeferredNotice>
      ) : (tiers.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Tier catalogue empty.
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {tiers.data?.items.map((tier) => (
            <article key={tier.code} className="bg-white border border-border rounded-xl p-5 space-y-3">
              <header>
                <p className="text-xs font-semibold text-nipost-green uppercase tracking-wide">
                  {tier.code}
                </p>
                <h2 className="text-lg font-bold text-gray-900">{tier.displayName}</h2>
                <p className="text-xs text-muted mt-1">{tier.description}</p>
              </header>
              <dl className="text-xs space-y-1">
                <div className="flex justify-between items-center">
                  <dt className="text-muted">Daily txn limit</dt>
                  <dd className="font-mono">
                    {editing === tier.code ? (
                      <span className="inline-flex items-center gap-2">
                        <input
                          type="text"
                          value={editLimit}
                          onChange={(e) => setEditLimit(e.target.value)}
                          className="px-2 py-1 border border-border rounded text-xs w-28 font-mono"
                          data-testid={`tier-limit-input-${tier.code}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setActionErr(null);
                            save.mutate(tier.code);
                          }}
                          disabled={save.isPending}
                          className="text-[11px] font-semibold text-nipost-green hover:underline disabled:opacity-50"
                          data-testid={`tier-save-${tier.code}`}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(null)}
                          className="text-[11px] text-muted hover:underline"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        ₦{tier.dailyTxnLimit}
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(tier.code);
                            setEditLimit(tier.dailyTxnLimit);
                          }}
                          className="text-[11px] text-nipost-green hover:underline"
                          data-testid={`tier-edit-${tier.code}`}
                        >
                          Edit
                        </button>
                      </span>
                    )}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Status</dt>
                  <dd>{tier.active ? 'Active' : 'Inactive'}</dd>
                </div>
              </dl>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1">
                  Allowed services ({tier.allowedServices.length})
                </p>
                <ul className="text-xs space-y-0.5 max-h-32 overflow-y-auto">
                  {tier.allowedServices.map((s) => (
                    <li key={s} className="font-mono">{s}</li>
                  ))}
                  {tier.allowedServices.length === 0 ? (
                    <li className="text-muted">—</li>
                  ) : null}
                </ul>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1">
                  Default permissions ({tier.defaultPermissions.length})
                </p>
                <ul className="text-xs space-y-0.5 max-h-32 overflow-y-auto">
                  {tier.defaultPermissions.map((p) => (
                    <li key={p} className="font-mono">{p}</li>
                  ))}
                  {tier.defaultPermissions.length === 0 ? (
                    <li className="text-muted">—</li>
                  ) : null}
                </ul>
              </div>
            </article>
          ))}
        </div>
      )}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}
    </div>
  );
}
